// src/pages/Admin/MOUDashboard.js
import { useEffect, useMemo, useState } from "react";
import {
  collection,
  onSnapshot,
  orderBy,
  query,
  updateDoc,
  doc,
  serverTimestamp,
  setDoc,
} from "firebase/firestore";
import { ref as storageRef, uploadBytes, getDownloadURL } from "firebase/storage";
import { db, storage } from "../../firebaseConfig";
import AdminGate from "../../components/AdminGate";
import jsPDF from "jspdf";

/* -------------------- helpers -------------------- */
function formatDate(ts) {
  try {
    if (!ts) return "—";
    if (typeof ts.toDate === "function") return ts.toDate().toLocaleString();
    if (ts instanceof Date) return ts.toLocaleString();
    if (typeof ts === "number") {
      const t = ts > 1e12 ? ts : ts * 1000;
      return new Date(t).toLocaleString();
    }
    const d = new Date(ts);
    return isNaN(d.getTime()) ? "—" : d.toLocaleString();
  } catch {
    return "—";
  }
}
function clsx(...xs) { return xs.filter(Boolean).join(" "); }
function downloadCSV(filename, rows) {
  if (!rows?.length) return;
  const seen = new Set(Object.keys(rows[0] || {}));
  rows.forEach(r => Object.keys(r||{}).forEach(k => seen.add(k)));
  const headers = Array.from(seen);
  const esc = (v) => `"${String(v ?? "").replace(/"/g,'""').replace(/\r?\n/g," ")}"`;
  const csv = [headers.join(","), ...rows.map(r => headers.map(h => esc(r[h])).join(","))].join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a"); a.href = url; a.download = filename; a.click(); URL.revokeObjectURL(url);
}
const StatusPill = ({ v }) => (
  <span className={clsx("inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold",
    v ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-700")}>
    {v ? "Generated" : "Not Generated"}
  </span>
);

/* ---------- Fallback MOU PDF (simple jsPDF) ---------- */
async function fallbackGenerateMOU(app) {
  const pdf = new jsPDF({ unit: "pt" });
  pdf.setFontSize(18);
  pdf.text("MEMORANDUM OF UNDERSTANDING", 40, 48);
  pdf.setFontSize(11);
  let y = 90;
  const L = (label, val) => { pdf.text(`${label}:`, 40, y); pdf.text(String(val ?? "—"), 160, y, { maxWidth: 380 }); y += 18; };
  L("Society", app.societyName);
  L("Applicant", app.applicantName);
  L("Reg No", app.regNo);
  L("Address", app.address || `${app.village || ""}, ${app.taluka || ""}, ${app.district || ""}`);
  L("Capacity (MT)", app.godownCapacity);
  L("Generated On", new Date().toLocaleString());
  pdf.setFontSize(10);
  pdf.text("This is an auto-generated MOU placeholder. Replace with your template later.", 40, y+10);
  return pdf.output("blob");
}

/* ===================== main page ===================== */
export default function MOUDashboard() {
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [qText, setQText] = useState("");
  const [stateFilter, setStateFilter] = useState("");
  const [districtFilter, setDistrictFilter] = useState("");
  const [sortKey, setSortKey] = useState("mouDate:desc");
  const [error, setError] = useState("");

  const [selected, setSelected] = useState(new Set());
  const [pageSize, setPageSize] = useState(10);
  const [page, setPage] = useState(1);

  const [editing, setEditing] = useState(null);
  const [editData, setEditData] = useState({});

  useEffect(() => {
    const qRef = query(collection(db, "applications"), orderBy("submittedAt", "desc"));
    const unsub = onSnapshot(qRef, (snap) => {
      const arr = snap.docs
        .map((d)=>({ id:d.id, ...d.data() }))
        .filter(a => String(a.status || "pending").toLowerCase() === "approved");
      setApps(arr); setLoading(false);
    }, (err)=>{
      console.error("[mou] snapshot error", err);
      setError("Failed to load applications."); setLoading(false);
    });
    return () => unsub();
  }, []);

  const states = useMemo(()=>Array.from(new Set(apps.map(a=>a.state).filter(Boolean))).sort(),[apps]);
  const districts = useMemo(()=>Array.from(new Set(apps.filter(a=>!stateFilter || a.state===stateFilter).map(a=>a.district).filter(Boolean))).sort(),[apps,stateFilter]);

  const filtered = useMemo(()=>{
    const t = qText.trim().toLowerCase();
    let rows = apps.filter(a=>{
      if (stateFilter && a.state!==stateFilter) return false;
      if (districtFilter && a.district!==districtFilter) return false;
      if (!t) return true;
      const hay = `${a.societyName||""} ${a.applicantName||""} ${a.district||""} ${a.village||""} ${a.regNo||""}`.toLowerCase();
      return hay.includes(t);
    });
    const [k, dir="asc"] = sortKey.split(":");
    rows.sort((a,b)=>{
      const m = dir==="asc" ? 1 : -1;
      if (k==="mouDate"){
        const aa = a.mouDate?.toDate ? a.mouDate.toDate().getTime() : new Date(a.mouDate||0).getTime();
        const bb = b.mouDate?.toDate ? b.mouDate.toDate().getTime() : new Date(b.mouDate||0).getTime();
        return (aa-bb)*m;
      }
      if (k==="submittedAt"){
        const aa = a.submittedAt?.toDate ? a.submittedAt.toDate().getTime() : new Date(a.submittedAt||0).getTime();
        const bb = b.submittedAt?.toDate ? b.submittedAt.toDate().getTime() : new Date(b.submittedAt||0).getTime();
        return (aa-bb)*m;
      }
      if (k==="name"){
        return String(a.societyName||a.applicantName||"").localeCompare(String(b.societyName||b.applicantName||""))*m;
      }
      return 0;
    });
    return rows;
  },[apps,qText,stateFilter,districtFilter,sortKey]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const pageData = filtered.slice((page-1)*pageSize, page*pageSize);

  function toggleSelect(id){
    const s = new Set(selected);
    if (s.has(id)) s.delete(id); else s.add(id);
    setSelected(s);
  }
  function selectAllOnPage(){
    const s = new Set(selected);
    const all = pageData.map(r=>r.id);
    const allSel = all.every(id=>s.has(id));
    if (allSel) all.forEach(id=>s.delete(id)); else all.forEach(id=>s.add(id));
    setSelected(s);
  }

  /* --------- Generate & Link (uses dynamic import inside) --------- */
  async function generateAndLink(app) {
    try {
      let fileBlob = null;
      let resultUrl = null;

      // Try project's generator if present
      try {
        const mod = await import("../../pdfs/generateMOU"); // no top-level await; works if file exists
        const customGenerateMOU = mod?.default || mod?.generateMOU;
        if (typeof customGenerateMOU === "function") {
          const r = await customGenerateMOU(app);
          if (r instanceof Blob) fileBlob = r;
          else if (typeof r === "string") resultUrl = r;
        }
      } catch { /* ignore, fallback below */ }

      // Fallback
      if (!fileBlob && !resultUrl) {
        fileBlob = await fallbackGenerateMOU(app);
      }

      // Upload if we got Blob
      if (fileBlob) {
        const safeId = (app.appId || app.id || "mou").toString().replace(/[^\w\-]+/g,"_");
        const path = `mou/${safeId}.pdf`;
        const sRef = storageRef(storage, path);
        await uploadBytes(sRef, fileBlob, { contentType: "application/pdf" });
        resultUrl = await getDownloadURL(sRef);
      }

      if (!resultUrl) { alert("MOU generated, but URL not available."); return; }

      // Write back
      const ref = doc(db, "applications", app.id);
      await updateDoc(ref, { mouUrl: resultUrl, mouDate: serverTimestamp() });

      // Audit
      await setDoc(doc(collection(db, "applications", app.id, "audits")), {
        action: "mou:generated", at: serverTimestamp(), url: resultUrl
      });
    } catch (e) {
      console.error("[generateAndLink] error", e?.code, e?.message, e);
      alert(`Generate failed: ${e?.code || ""} ${e?.message || ""}`);
    }
  }

  async function bulkGenerate() {
    if (selected.size === 0) { alert("Select at least one approved application."); return; }
    if (!confirm(`Generate MOU for ${selected.size} item(s)?`)) return;
    try {
      for (const id of selected) {
        // eslint-disable-next-line no-await-in-loop
        await generateAndLink(apps.find(a=>a.id===id));
      }
      alert("Bulk MOU generation finished.");
    } catch (e) {
      console.error("[bulkGenerate] error", e);
      alert("Bulk generation failed (see console).");
    }
  }

  function exportCSV() {
    const rows = filtered.map(r=>({
      AppID: r.appId || r.id,
      SocietyName: r.societyName || "",
      Applicant: r.applicantName || "",
      District: r.district || "",
      Capacity_MT: r.godownCapacity || "",
      SubmittedAt: formatDate(r.submittedAt),
      MOU_Date: formatDate(r.mouDate),
      MOU_URL: r.mouUrl || ""
    }));
    downloadCSV(`mou_${new Date().toISOString().slice(0,10)}.csv`, rows);
  }

  return (
    <AdminGate>
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
        <div className="max-w-7xl mx-auto px-4 py-6">
          {/* Logout Bar */}
<div className="flex justify-end mb-4">
  <button
    onClick={() => {
      import("../../services/adminAuth").then(({ adminLogout }) => {
        adminLogout();
        window.location.href = "/admin-login";
      });
    }}
    className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
  >
    Logout
  </button>
</div>

          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-6">
            <div>
              <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight text-transparent bg-clip-text bg-gradient-to-r from-emerald-700 via-teal-600 to-blue-600">
                MOU Dashboard
              </h1>
              <p className="text-sm text-slate-600 mt-1">Generate, link, and manage MOUs for approved applications.</p>
            </div>
            <div className="flex gap-3">
              <button onClick={exportCSV} className="px-4 py-2 rounded-xl border border-slate-300 bg-white text-slate-700 hover:bg-slate-50">Export CSV</button>
              <button onClick={() => window.location.reload()} className="px-4 py-2 rounded-xl border border-slate-300 bg-white text-slate-700 hover:bg-slate-50">Refresh</button>
            </div>
          </div>

          {/* Toolbar */}
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4 mb-5">
            <div className="flex flex-col lg:flex-row gap-3 lg:items-center lg:justify-between">
              <div className="flex items-center gap-3 flex-1">
                <input
                  value={qText}
                  onChange={(e)=>{ setQText(e.target.value); setPage(1); }}
                  placeholder="Search society / applicant / reg no / district…"
                  className="flex-1 min-w-[220px] border border-slate-300 rounded-xl px-4 py-2 text-sm focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                />
                <select value={stateFilter} onChange={(e)=>{ setStateFilter(e.target.value); setDistrictFilter(""); setPage(1); }} className="border border-slate-300 rounded-xl px-3 py-2 text-sm">
                  <option value="">All States</option>
                  {states.map(s=> <option key={s} value={s}>{s}</option>)}
                </select>
                <select value={districtFilter} onChange={(e)=>{ setDistrictFilter(e.target.value); setPage(1); }} className="border border-slate-300 rounded-xl px-3 py-2 text-sm">
                  <option value="">All Districts</option>
                  {districts.map(d=> <option key={d} value={d}>{d}</option>)}
                </select>
                <select value={sortKey} onChange={(e)=>setSortKey(e.target.value)} className="border border-slate-300 rounded-xl px-3 py-2 text-sm">
                  <option value="mouDate:desc">MOU Date ↓</option>
                  <option value="mouDate:asc">MOU Date ↑</option>
                  <option value="submittedAt:desc">Submitted ↓</option>
                  <option value="submittedAt:asc">Submitted ↑</option>
                  <option value="name:asc">Name A→Z</option>
                  <option value="name:desc">Name Z→A</option>
                </select>
              </div>
              <div className="flex gap-2">
                <button onClick={bulkGenerate} className="px-4 py-2 rounded-xl bg-gradient-to-r from-emerald-600 to-teal-600 text-white hover:shadow">Generate MOU (Selected)</button>
              </div>
            </div>
          </div>

          {/* Error */}
          {error && <div className="mb-4 rounded-xl border border-rose-200 bg-rose-50 text-rose-700 px-4 py-3">{error}</div>}

          {/* Table */}
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="w-full overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="p-3"><input type="checkbox" onChange={selectAllOnPage} checked={pageData.length>0 && pageData.every(r=>selected.has(r.id))} /></th>
                    <th className="p-3 text-left">Society / Applicant</th>
                    <th className="p-3 text-left">Location</th>
                    <th className="p-3 text-left">Capacity</th>
                    <th className="p-3 text-left">Submitted</th>
                    <th className="p-3 text-left">MOU</th>
                    <th className="p-3 text-left w-64">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan={7} className="p-8 text-center text-slate-500">Loading…</td></tr>
                  ) : pageData.length === 0 ? (
                    <tr><td colSpan={7} className="p-8 text-center text-slate-500">No approved applications.</td></tr>
                  ) : (
                    pageData.map(r=>{
                      const generated = !!r.mouUrl;
                      return (
                        <tr key={r.id} className="border-t hover:bg-slate-50">
                          <td className="p-3"><input type="checkbox" checked={selected.has(r.id)} onChange={()=>toggleSelect(r.id)} /></td>
                          <td className="p-3">
                            <div className="font-semibold text-slate-900">{r.societyName || "—"}</div>
                            <div className="text-xs text-slate-500">{r.applicantName || "—"} • {r.email || r.submittedByEmail || "—"}</div>
                          </td>
                          <td className="p-3">
                            <div className="text-slate-900">{r.state || "—"} / {r.district || "—"}</div>
                            <div className="text-xs text-slate-500">{r.taluka || "—"} • {r.village || "—"} • {r.pincode || "—"}</div>
                          </td>
                          <td className="p-3">{r.godownCapacity ? `${r.godownCapacity} MT` : "—"}</td>
                          <td className="p-3 text-xs text-slate-600">{formatDate(r.submittedAt)}</td>
                          <td className="p-3">
                            <div className="flex flex-col">
                              <StatusPill v={generated}/>
                              <span className="text-[11px] text-slate-500 mt-1">{formatDate(r.mouDate)}</span>
                            </div>
                          </td>
                          <td className="p-3">
                            <div className="flex flex-wrap gap-2">
                              <button onClick={()=>generateAndLink(r)} className="px-3 py-1 bg-emerald-600 text-white rounded-lg text-xs hover:bg-emerald-700">
                                {generated ? "Re-generate" : "Generate"}
                              </button>
                              {generated && (
                                <>
                                  <a href={r.mouUrl} target="_blank" rel="noreferrer" className="px-3 py-1 bg-blue-600 text-white rounded-lg text-xs hover:bg-blue-700">Open</a>
                                  <a href={r.mouUrl} download className="px-3 py-1 bg-indigo-600 text-white rounded-lg text-xs hover:bg-indigo-700">Download</a>
                                </>
                              )}
                              <button
                                onClick={()=>{ setEditing(r); setEditData(r); }}
                                className="px-3 py-1 bg-slate-200 text-slate-800 rounded-lg text-xs hover:bg-slate-300"
                              >
                                Edit
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination footer */}
            <div className="flex items-center justify-between p-4 border-t bg-slate-50">
              <div className="text-xs text-slate-600">Showing {(page-1)*pageSize+1}–{Math.min(page*pageSize, filtered.length)} of {filtered.length}</div>
              <div className="flex items-center gap-2">
                <button className="px-3 py-1 border border-slate-300 rounded-lg text-sm hover:bg-slate-100 disabled:opacity-50" onClick={()=>setPage(1)} disabled={page===1}>First</button>
                <button className="px-3 py-1 border border-slate-300 rounded-lg text-sm hover:bg-slate-100 disabled:opacity-50" onClick={()=>setPage(p=>Math.max(1,p-1))} disabled={page===1}>Prev</button>
                <div className="px-3 py-1 text-sm text-slate-700">{page} / {totalPages}</div>
                <button className="px-3 py-1 border border-slate-300 rounded-lg text-sm hover:bg-slate-100 disabled:opacity-50" onClick={()=>setPage(p=>Math.min(totalPages,p+1))} disabled={page===totalPages}>Next</button>
                <button className="px-3 py-1 border border-slate-300 rounded-lg text-sm hover:bg-slate-100 disabled:opacity-50" onClick={()=>setPage(totalPages)} disabled={page===totalPages}>Last</button>
                <select value={pageSize} onChange={(e)=>{ setPageSize(Number(e.target.value)); setPage(1); }} className="ml-2 border border-slate-300 rounded-lg px-2 py-1 text-sm">
                  <option value={5}>5 / page</option>
                  <option value={10}>10 / page</option>
                  <option value={20}>20 / page</option>
                </select>
              </div>
            </div>
          </div>

          {/* Edit modal */}
          {editing && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
              <div className="bg-white rounded-2xl w-full max-w-3xl p-6 relative shadow-2xl">
                <button className="absolute top-4 right-4 text-2xl text-slate-500 hover:text-slate-700" onClick={()=>setEditing(null)}>×</button>
                <h3 className="text-lg font-bold text-slate-900 mb-4">Edit Application — {editing.appId || editing.id}</h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <TextField label="Society Name" value={editData.societyName} onChange={(v)=>setEditData(x=>({...x, societyName:v}))}/>
                  <TextField label="Registration No." value={editData.regNo} onChange={(v)=>setEditData(x=>({...x, regNo:v}))}/>
                  <TextField label="Applicant Name" value={editData.applicantName} onChange={(v)=>setEditData(x=>({...x, applicantName:v}))}/>
                  <TextField label="Email" value={editData.email} onChange={(v)=>setEditData(x=>({...x, email:v}))}/>
                  <TextArea  label="Address" value={editData.address} onChange={(v)=>setEditData(x=>({...x, address:v}))} rows={3}/>
                  <TextField label="District" value={editData.district} onChange={(v)=>setEditData(x=>({...x, district:v}))}/>
                  <TextField label="Taluka" value={editData.taluka} onChange={(v)=>setEditData(x=>({...x, taluka:v}))}/>
                  <TextField label="Village" value={editData.village} onChange={(v)=>setEditData(x=>({...x, village:v}))}/>
                  <TextField label="Pincode" value={editData.pincode} onChange={(v)=>setEditData(x=>({...x, pincode:v}))}/>
                  <TextField label="Godown Capacity (MT)" value={editData.godownCapacity} onChange={(v)=>setEditData(x=>({...x, godownCapacity:v}))}/>
                </div>

                <div className="mt-6 text-right">
                  <button className="px-4 py-2 rounded-lg bg-slate-200 text-slate-800 hover:bg-slate-300 mr-2" onClick={()=>setEditing(null)}>Cancel</button>
                  <button
                    className="px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700"
                    onClick={async ()=>{
                      try{
                        await updateDoc(doc(db,"applications",editing.id), editData);
                        setEditing(null);
                      }catch(e){
                        console.error("[edit save] error", e);
                        alert("Failed to save edits.");
                      }
                    }}
                  >Save</button>
                </div>
              </div>
            </div>
          )}

        </div>
      </div>
    </AdminGate>
  );
}

/* ---------- small inputs ---------- */
function TextField({ label, value, onChange }) {
  return (
    <div>
      <label className="block text-xs font-medium text-slate-600 mb-1">{label}</label>
      <input
        value={value || ""}
        onChange={(e)=>onChange(e.target.value)}
        className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
      />
    </div>
  );
}
function TextArea({ label, value, onChange, rows=3 }) {
  return (
    <div className="md:col-span-2">
      <label className="block text-xs font-medium text-slate-600 mb-1">{label}</label>
      <textarea
        rows={rows}
        value={value || ""}
        onChange={(e)=>onChange(e.target.value)}
        className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
      />
    </div>
  );
}
